/* Canvas rendering: grid, walls, openings, fixtures, dims, constraint badges,
   tool previews. Also view helpers (zoom/fit) and PNG export. */
import { OPENING_DEFS } from './types.ts';
import type { OpeningType, Wall } from './types.ts';
import { $, state } from './state.ts';
import { clamp, fmtLen, getUnits, IN_PER_CM, rad } from './utils.ts';
import { constraintDist, docBBox, nearestWall, wallClear, wallPt, wallVec, WELD_TOL } from './geom.ts';
import { FIXTURES } from './fixtures.ts';
import { snapPt, snapV, wallDraftEnd } from './snap.ts';
import { clearSize, normRect, ROOM_MIN, ROOM_TH } from './room.ts';
import { detectRooms, labelAnchor } from './rooms.ts';
import { initials, others } from './presence.ts';
import { fmtArea } from './utils.ts';

/** Rooms are recomputed only when the walls actually change. */
let roomsSig = '';
export function refreshRooms(): void {
  const sig = state.doc.walls.map(w => `${w.id}${w.x1},${w.y1},${w.x2},${w.y2},${w.th}`).join('|');
  if (sig === roomsSig) return;
  roomsSig = sig;
  try { state.rooms = detectRooms(state.doc.walls); } catch { state.rooms = []; }
}
import { findItem } from './state.ts';

export const canvas = $('#c') as HTMLCanvasElement;
export const ctx = canvas.getContext('2d')!;
export let CSS_W = 0, CSS_H = 0;

export function resize(): void {
  const wrap = $('#canvasWrap'), dpr = window.devicePixelRatio || 1;
  CSS_W = wrap.clientWidth; CSS_H = wrap.clientHeight;
  canvas.width = Math.round(CSS_W * dpr); canvas.height = Math.round(CSS_H * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  requestDraw();
}

export interface Colors {
  bg: string; grid: string; grid2: string; ink: string; wall: string; accent: string;
  accentSoft: string; muted: string; dim: string; panel: string; danger: string;
}
let COLORS: Colors | null = null;
export function palette(): Colors {
  if (COLORS) return COLORS;
  const cs = getComputedStyle(document.documentElement);
  const g = (n: string) => cs.getPropertyValue(n).trim();
  COLORS = { bg: g('--bg'), grid: g('--grid'), grid2: g('--grid2'), ink: g('--ink'), wall: g('--wall'),
             accent: g('--accent'), accentSoft: g('--accent-soft'), muted: g('--muted'), dim: g('--dim'),
             panel: g('--panel'), danger: g('--danger') };
  return COLORS;
}
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => { COLORS = null; requestDraw(); });

/** Bottom-bar View popup toggles: state + persistence + checkbox stay in sync. */
export function setViewOpt(key: 'dims' | 'badges' | 'labels' | 'areas' | 'below', on: boolean): void {
  state.show[key] = on;
  try { localStorage.setItem('playout:view', JSON.stringify(state.show)); } catch { /* private mode */ }
  const chk = document.querySelector('#view' + key[0].toUpperCase() + key.slice(1)) as HTMLInputElement | null;
  if (chk) chk.checked = on;
  requestDraw();
}

let needsDraw = false;
export function requestDraw(): void {
  if (needsDraw) return;
  needsDraw = true;
  requestAnimationFrame(() => { needsDraw = false; draw(); });
}

/** Synchronous draw (tests measure frame cost with it). */
export const drawNow = (): void => draw();
function draw(): void {
  const C = palette(), v = state.view;
  drawScene(ctx, { z: v.zoom, ox: v.x, oy: v.y, w: CSS_W, h: CSS_H }, C, { live: true });
}

interface View { z: number; ox: number; oy: number; w: number; h: number }
interface DrawOpts { live: boolean; grid?: boolean; dims?: boolean }

const fmtDeg = (d: number) => (Math.round(d * 10) / 10).toString().replace(/\.0$/, '');

/* measureText is one of the slowest canvas calls and dims/badges re-measure
   the same strings every frame — memoize by font+text. */
const twCache = new Map<string, number>();
function textW(g: CanvasRenderingContext2D, t: string): number {
  const k = g.font + '|' + t;
  let v = twCache.get(k);
  if (v === undefined) {
    v = g.measureText(t).width;
    if (twCache.size > 4000) twCache.clear();
    twCache.set(k, v);
  }
  return v;
}

/* Background layer (bg fill + grid) cache. It only changes with the view /
   theme / canvas size — never while dragging geometry — so live frames blit
   one image instead of re-stroking hundreds of antialiased hairlines (the
   difference between ~10ms and ~100ms a frame on software-rendered canvas). */
let bgCache: HTMLCanvasElement | null = null;
let bgKey = '';

function drawGrid(g: CanvasRenderingContext2D, view: View, C: Colors): void {
  const { z, ox, oy, w: W, h: H } = view;
  const X = (wx: number) => (wx - ox) * z, Y = (wy: number) => (wy - oy) * z;
  const metric = getUnits() === 'm';
  const steps = metric ? [1, 2, 5, 10, 20, 50, 100, 200, 500].map(cm => cm * IN_PER_CM) : [1, 2, 3, 6, 12, 24, 48, 96, 192];
  const minor = steps.find(s => s * z >= 9) || steps[steps.length - 1];
  const major = metric ? (minor < 100 * IN_PER_CM - 1e-9 ? 100 * IN_PER_CM : minor * 5) : (minor < 12 ? 12 : minor * 4);
  for (const [sp, col] of [[minor, C.grid], [major, C.grid2]] as [number, string][]) {
    g.strokeStyle = col; g.lineWidth = 1; g.beginPath();
    const sx0 = Math.floor(ox / sp) * sp;
    for (let x = sx0; X(x) <= W; x += sp) { const px = Math.round(X(x)) + .5; g.moveTo(px, 0); g.lineTo(px, H); }
    const sy0 = Math.floor(oy / sp) * sp;
    for (let y = sy0; Y(y) <= H; y += sp) { const py = Math.round(Y(y)) + .5; g.moveTo(0, py); g.lineTo(W, py); }
    g.stroke();
  }
}

export function drawScene(g: CanvasRenderingContext2D, view: View, C: Colors, opts: DrawOpts): void {
  const { z, ox, oy, w: W, h: H } = view;
  const X = (wx: number) => (wx - ox) * z, Y = (wy: number) => (wy - oy) * z;
  g.save();

  /* ---- background + grid (cached for the live canvas) ---- */
  if (opts.live) {
    const dpr = window.devicePixelRatio || 1;
    const key = [z, ox, oy, W, H, dpr, C.bg, C.grid, C.grid2, opts.grid !== false, getUnits()].join('|');
    bgCache ||= document.createElement('canvas');
    if (key !== bgKey) {
      const pw = Math.round(W * dpr), ph = Math.round(H * dpr);
      if (bgCache.width !== pw || bgCache.height !== ph) { bgCache.width = pw; bgCache.height = ph; }
      const og = bgCache.getContext('2d')!;
      og.setTransform(dpr, 0, 0, dpr, 0, 0);
      og.fillStyle = C.bg; og.fillRect(0, 0, W, H);
      if (opts.grid !== false) drawGrid(og, view, C);
      bgKey = key;
    }
    g.drawImage(bgCache, 0, 0, W, H);
  } else {
    g.fillStyle = C.bg; g.fillRect(0, 0, W, H);
    if (opts.grid !== false) drawGrid(g, view, C);
  }

  const doc = state.doc, sel = opts.live ? state.sel : new Set<string>();
  const hover = opts.live ? state.hover : null;

  /* ---- the floor below, faint, for lining walls up ---- */
  if (opts.live && state.show.below && state.underlay) {
    g.save();
    g.lineCap = 'square'; g.strokeStyle = C.muted; g.globalAlpha = .22;
    for (const wl of state.underlay.doc.walls) {
      g.lineWidth = Math.max(1, wl.th * z);
      g.beginPath(); g.moveTo(X(wl.x1), Y(wl.y1)); g.lineTo(X(wl.x2), Y(wl.y2)); g.stroke();
    }
    g.restore();
  }

  /* ---- walls ---- */
  // square caps extend th/2 past each endpoint, filling corner joints —
  // the 3D view (vr.html buildWall) extends wall ends the same way
  g.lineCap = 'square';
  for (const wl of doc.walls) {
    g.strokeStyle = C.wall; g.lineWidth = Math.max(1.5, wl.th * z);
    g.beginPath(); g.moveTo(X(wl.x1), Y(wl.y1)); g.lineTo(X(wl.x2), Y(wl.y2)); g.stroke();
  }
  /* ---- openings: punch gaps + symbols ---- */
  for (const o of doc.openings) {
    const wl = doc.walls.find(w => w.id === o.wallId);
    if (!wl) continue;
    const v2 = wallVec(wl), c = wallPt(wl, o.t), ang = Math.atan2(v2.uy, v2.ux);
    const hw = o.width / 2, thpx = wl.th * z;
    g.save();
    g.translate(X(c.x), Y(c.y)); g.rotate(ang);
    g.fillStyle = C.bg;
    g.fillRect(-hw * z, -thpx / 2 - 1, o.width * z, thpx + 2);
    // jambs
    g.strokeStyle = C.ink; g.lineWidth = 1.2; g.lineCap = 'butt';
    g.beginPath();
    g.moveTo(-hw * z, -thpx / 2); g.lineTo(-hw * z, thpx / 2);
    g.moveTo(hw * z, -thpx / 2); g.lineTo(hw * z, thpx / 2);
    g.stroke();
    if (o.type === 'window') {
      g.beginPath();
      for (const off of [-thpx / 2, 0, thpx / 2]) { g.moveTo(-hw * z, off); g.lineTo(hw * z, off); }
      g.stroke();
    }
    if (o.type === 'slider') {
      // bypass panels: each covers just over half the opening, offset to a side
      g.lineWidth = Math.max(2, thpx * 0.26);
      g.beginPath(); g.moveTo(-hw * z, -thpx / 4); g.lineTo(2 * z, -thpx / 4); g.stroke();
      g.beginPath(); g.moveTo(-2 * z, thpx / 4); g.lineTo(hw * z, thpx / 4); g.stroke();
      g.lineWidth = 1.2;
    }
    if (o.type === 'pocket') {
      // leaf half-open from the pocket jamb; dashed cavity inside the wall beyond it
      const side = o.hinge ? 1 : -1;
      g.lineWidth = Math.max(2, thpx * 0.26);
      g.beginPath(); g.moveTo(side * hw * z, 0); g.lineTo(0, 0); g.stroke();
      g.strokeStyle = C.bg; g.lineWidth = Math.max(1.5, thpx * 0.2); g.setLineDash([4, 3]);
      g.beginPath(); g.moveTo(side * hw * z, 0); g.lineTo(side * (hw + o.width) * z, 0); g.stroke();
      g.setLineDash([]); g.strokeStyle = C.ink; g.lineWidth = 1.2;
    }
    if (o.type === 'bifold') {
      // standard bifold symbol: half-folded panels as V peaks on the swing side
      // (one pair ≤42″, else a double pair folding to each jamb)
      const s = o.swing || 1;
      const units = o.width > 42 ? 2 : 1, uw = o.width / units;
      g.lineWidth = 1.4;
      g.beginPath();
      for (let i = 0; i < units; i++) {
        const a = -hw + i * uw, b = a + uw;
        g.moveTo(a * z, 0); g.lineTo((a + b) / 2 * z, s * uw * 0.4 * z); g.lineTo(b * z, 0);
      }
      g.stroke();
      g.lineWidth = 1.2;
    }
    if (o.type === 'double') {
      // French pair: a leaf fully open at each jamb + dashed quarter-arc swings
      const s = o.swing || 1;
      for (const sd of [-1, 1]) {
        const jx = sd * hw * z;
        g.strokeStyle = C.ink; g.lineWidth = 1.4; g.setLineDash([]);
        g.beginPath(); g.moveTo(jx, 0); g.lineTo(jx, s * hw * z); g.stroke();
        g.strokeStyle = C.dim; g.lineWidth = 1; g.setLineDash([4, 3]);
        g.beginPath(); g.arc(jx, 0, hw * z, sd < 0 ? 0 : Math.PI, s * Math.PI / 2, sd * s > 0);
        g.stroke();
      }
      g.setLineDash([]); g.strokeStyle = C.ink; g.lineWidth = 1.2;
    }
    if (o.type === 'cased') {
      // dashed lines at the wall faces: open passage, wall continues above
      g.setLineDash([4, 3]);
      g.beginPath();
      for (const off of [-thpx / 2, thpx / 2]) { g.moveTo(-hw * z, off); g.lineTo(hw * z, off); }
      g.stroke();
      g.setLineDash([]);
    }
    g.restore();
    if (o.type === 'door') {
      const hingeT = o.t + (o.hinge ? hw : -hw);
      const hp = wallPt(wl, hingeT);
      const sw = o.swing || 1;
      const jambDir = o.hinge ? -1 : 1;   // toward other jamb, along wall
      const svx = v2.ux * jambDir, svy = v2.uy * jambDir;
      const evx = v2.nx * sw, evy = v2.ny * sw;
      const a0 = Math.atan2(svy, svx), a1 = Math.atan2(evy, evx);
      let dAng = (a1 - a0) % (Math.PI * 2); if (dAng < 0) dAng += Math.PI * 2;
      g.strokeStyle = C.ink; g.lineWidth = 1.4; g.lineCap = 'butt';
      g.beginPath();
      g.moveTo(X(hp.x), Y(hp.y));
      g.lineTo(X(hp.x + evx * o.width), Y(hp.y + evy * o.width));
      g.stroke();
      g.strokeStyle = C.dim; g.lineWidth = 1; g.setLineDash([4, 3]);
      g.beginPath();
      g.arc(X(hp.x), Y(hp.y), o.width * z, a0, a1, dAng > Math.PI);
      g.stroke();
      g.setLineDash([]);
    }
    if (opts.live && (sel.has(o.id) || hover === o.id)) {
      g.save();
      g.translate(X(c.x), Y(c.y)); g.rotate(ang);
      g.strokeStyle = C.accent; g.lineWidth = 2;
      g.strokeRect(-hw * z - 3, -thpx / 2 - 4, o.width * z + 6, thpx + 8);
      // jamb handles on a selected door/pocket: filled = hinge side, hollow = click to move the hinge here
      if (sel.has(o.id) && (o.type === 'door' || o.type === 'pocket')) {
        g.lineWidth = 1.5;
        for (const end of [0, 1]) {
          g.beginPath(); g.arc(end ? hw * z : -hw * z, 0, 5, 0, 7);
          g.fillStyle = (end === 1) === !!o.hinge ? C.accent : C.panel; g.fill(); g.stroke();
        }
      }
      g.restore();
    }
  }
  /* wall selection overlays */
  if (opts.live) for (const wl of doc.walls) {
    if (!sel.has(wl.id) && hover !== wl.id) continue;
    g.strokeStyle = sel.has(wl.id) ? C.accent : C.accentSoft;
    g.globalAlpha = sel.has(wl.id) ? .55 : 1;
    g.lineWidth = wl.th * z + 4; g.lineCap = 'square';
    g.beginPath(); g.moveTo(X(wl.x1), Y(wl.y1)); g.lineTo(X(wl.x2), Y(wl.y2)); g.stroke();
    g.globalAlpha = 1;
  }

  /* ---- fixtures ---- */
  for (const f of doc.fixtures) {
    const def = FIXTURES[f.type] || FIXTURES.box;
    g.save();
    g.translate(X(f.x), Y(f.y)); g.rotate(rad(f.rot || 0)); g.scale(z, z);
    g.strokeStyle = C.ink; g.lineWidth = 1.4 / z; g.lineCap = 'round'; g.lineJoin = 'round';
    def.draw(g, f.w, f.h);
    g.restore();
    if (f.label && state.show.labels) {
      g.save();
      g.fillStyle = C.muted; g.font = '11px "Avenir Next",system-ui,sans-serif';
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(f.label, X(f.x), Y(f.y));
      g.restore();
    }
    if (opts.live && (sel.has(f.id) || hover === f.id)) {
      drawFixtureSel(g, f, X, Y, z, C, sel.has(f.id));
    }
  }

  /* ---- room areas: quiet text deep inside each enclosed room ---- */
  refreshRooms();
  if (state.show.areas && opts.dims !== false) for (const r of state.rooms) {
    const txt = r.rect ? `${fmtLen(r.w)} × ${fmtLen(r.h)}` : fmtArea(r.inside);
    const sub = r.rect ? fmtArea(r.inside) : '';
    // the room's own label (if it has one): tuck the size underneath it
    const a = labelAnchor(r, state.show.labels ? doc.labels : []);
    const px = Math.max(8.5, Math.min(12, 3 * z));
    const y0 = Y(a.y) + (a.under ? Math.max(9, (a.under.size || 8) * z) / 2 + px * .9 : 0);
    g.save();
    g.fillStyle = C.muted; g.globalAlpha = .85;
    g.font = `500 ${px}px "Avenir Next",system-ui,sans-serif`;
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(txt, X(a.x), y0);
    if (sub) { g.font = `${px * .9}px "Avenir Next",system-ui,sans-serif`; g.fillText(sub, X(a.x), y0 + px * 1.15); }
    g.restore();
  }

  /* ---- labels ---- */
  if (state.show.labels) for (const l of doc.labels) {
    const px = Math.max(9, (l.size || 8) * z);
    g.fillStyle = C.ink;
    g.font = `600 ${px}px "Avenir Next",system-ui,sans-serif`;
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(l.text, X(l.x), Y(l.y));
    const tw = textW(g, l.text);
    l._bb = { x: X(l.x) - tw / 2 - 4, y: Y(l.y) - px / 2 - 3, w: tw + 8, h: px + 6 };
    if (opts.live && (sel.has(l.id) || hover === l.id)) {
      g.strokeStyle = C.accent; g.lineWidth = sel.has(l.id) ? 1.6 : 1;
      g.setLineDash(sel.has(l.id) ? [] : [3, 3]);
      g.strokeRect(l._bb.x, l._bb.y, l._bb.w, l._bb.h);
      g.setLineDash([]);
    }
  }

  /* ---- wall dimensions ---- */
  const showDims = opts.dims !== false && state.show.dims;
  const showBadges = opts.dims !== false && state.show.badges;
  if (!showDims) for (const wl of doc.walls) wl._hitLen = wl._hitClear = null;
  if (!showBadges) for (const c of (doc.constraints || [])) { c._hit = null; if (c.type === 'angle') c._hit2 = null; }
  if (showDims) {
    g.font = '10.5px ui-monospace,Menlo,monospace';
    for (const wl of doc.walls) {
      const v2 = wallVec(wl);
      if (v2.len * z < 46) { wl._hitLen = wl._hitClear = null; continue; }
      const clear = wallClear(doc.walls, wl);
      const showClear = v2.len - clear > .3 && clear > 0 && v2.len * z >= 64;
      const mx = (wl.x1 + wl.x2) / 2, my = (wl.y1 + wl.y2) / 2;
      const off = (wl.th / 2) * z + (showClear ? 19 : 13);
      let ang = Math.atan2(v2.uy, v2.ux);
      const nx = v2.nx, ny = v2.ny;
      if (ang > Math.PI / 2 || ang <= -Math.PI / 2) { ang += Math.PI; }
      const tx = X(mx) - nx * off, ty = Y(my) - ny * off;
      g.save();
      g.translate(tx, ty); g.rotate(ang);
      const txt = (wl.locked ? '🔒 ' : '') + fmtLen(v2.len);
      const txt2 = showClear ? fmtLen(clear) + ' clear' : null;
      g.font = '10.5px ui-monospace,Menlo,monospace';
      const tw1 = textW(g, txt);
      let tw = tw1, tw2 = 0;
      if (txt2) {
        g.font = '9.5px ui-monospace,Menlo,monospace';
        tw2 = textW(g, txt2);
        tw = Math.max(tw, tw2);
      }
      const bh = txt2 ? 28 : 15, by = txt2 ? -14 : -8;
      g.fillStyle = C.bg; g.globalAlpha = .85; g.fillRect(-tw / 2 - 3, by, tw + 6, bh); g.globalAlpha = 1;
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = C.dim;
      g.font = '10.5px ui-monospace,Menlo,monospace';
      g.fillText(txt, 0, txt2 ? -6.5 : 0);
      if (txt2) {
        g.fillStyle = C.muted;
        g.font = '9.5px ui-monospace,Menlo,monospace';
        g.fillText(txt2, 0, 7);
      }
      g.restore();
      if (opts.live) {
        // Fusion-style: the dimension text itself is a click target
        const rot = (lx: number, ly: number) => ({ x: tx + lx * Math.cos(ang) - ly * Math.sin(ang),
                                                   y: ty + lx * Math.sin(ang) + ly * Math.cos(ang) });
        const pm = rot(0, txt2 ? -6.5 : 0);
        wl._hitLen = { x: pm.x, y: pm.y, ang, hw: Math.max(tw1 / 2 + 4, 12), hh: 8 };
        if (txt2) { const pc = rot(0, 7); wl._hitClear = { x: pc.x, y: pc.y, ang, hw: Math.max(tw2 / 2 + 4, 11), hh: 7.5 }; }
        else wl._hitClear = null;
      }
    }
  }
  /* ---- constraint badges & ties ---- */
  if (showBadges) {
    for (const c of (doc.constraints || [])) {
      if (c.type === 'dist') {
        const A = doc.walls.find(w => w.id === c.a), B = doc.walls.find(w => w.id === c.b);
        if (!A || !B) continue;
        const vA = wallVec(A);
        const midB = { x: (B.x1 + B.x2) / 2, y: (B.y1 + B.y2) / 2 };
        const tA = clamp((midB.x - A.x1) * vA.ux + (midB.y - A.y1) * vA.uy, 0, vA.len);
        const pa = wallPt(A, tA);
        const s = constraintDist(A, B);
        const pb = { x: pa.x + vA.nx * s, y: pa.y + vA.ny * s };
        const ax = X(pa.x), ay = Y(pa.y), bx = X(pb.x), by = Y(pb.y);
        g.strokeStyle = C.accent; g.lineWidth = 1.1; g.setLineDash([2, 3]);
        g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
        g.setLineDash([]);
        // end ticks along the walls
        const tx2 = vA.ux, ty2 = vA.uy;
        g.beginPath();
        for (const [px, py] of [[ax, ay], [bx, by]] as const) {
          g.moveTo(px - tx2 * 4, py - ty2 * 4); g.lineTo(px + tx2 * 4, py + ty2 * 4);
        }
        g.stroke();
        // label: clear distance + link mark, rotated with the tie
        const clearD = Math.abs(s) - A.th / 2 - B.th / 2;
        let langle = Math.atan2(by - ay, bx - ax) + Math.PI / 2;
        if (langle > Math.PI / 2 || langle <= -Math.PI / 2) langle += Math.PI;
        g.save();
        g.translate((ax + bx) / 2, (ay + by) / 2); g.rotate(langle);
        g.font = '10px ui-monospace,Menlo,monospace';
        const ltxt = '🔗 ' + fmtLen(clearD);
        const ltw = textW(g, ltxt);
        g.fillStyle = C.bg; g.globalAlpha = .85; g.fillRect(-ltw / 2 - 3, -8, ltw + 6, 15); g.globalAlpha = 1;
        g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(ltxt, 0, 0);
        g.restore();
        if (opts.live) c._hit = { x: (ax + bx) / 2, y: (ay + by) / 2, ang: langle, hw: Math.max(ltw / 2 + 6, 14), hh: 9 };
      }
      else if (c.type === 'angle') {
        const A = doc.walls.find(w => w.id === c.a), B = doc.walls.find(w => w.id === c.b);
        if (!A || !B) continue;
        const midA = { x: (A.x1 + A.x2) / 2, y: (A.y1 + A.y2) / 2 };
        const midB = { x: (B.x1 + B.x2) / 2, y: (B.y1 + B.y2) / 2 };
        let corner: { x: number; y: number } | null = null;
        for (const [axp, ayp] of [[A.x1, A.y1], [A.x2, A.y2]] as const)
          for (const [bxp, byp] of [[B.x1, B.y1], [B.x2, B.y2]] as const)
            if (Math.hypot(axp - bxp, ayp - byp) < WELD_TOL) corner = { x: axp, y: ayp };
        const btxt = c.deg === 90 ? '⊥' : c.deg === 0 ? '∥' : '∠' + fmtDeg(c.deg) + '°';
        g.font = '11px ui-monospace,Menlo,monospace';
        const btw = textW(g, btxt);
        const badge = (px: number, py: number) => {
          g.fillStyle = C.bg; g.globalAlpha = .85; g.fillRect(px - btw / 2 - 3, py - 8, btw + 6, 16); g.globalAlpha = 1;
          g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'middle';
          g.fillText(btxt, px, py);
          return { x: px, y: py, ang: 0, hw: Math.max(btw / 2 + 5, 11), hh: 9 };
        };
        if (corner) {
          // one badge tucked into the joint
          let dx = (midA.x - corner.x) + (midB.x - corner.x), dy = (midA.y - corner.y) + (midB.y - corner.y);
          const dl = Math.hypot(dx, dy) || 1; dx /= dl; dy /= dl;
          const h = badge(X(corner.x) + dx * 22, Y(corner.y) + dy * 22);
          if (opts.live) { c._hit = h; c._hit2 = null; }
        } else {
          // detached pair (the usual ∥ case): mark each wall, Fusion-style
          const hs = [A, B].map(wlq => {
            const vq = wallVec(wlq);
            return badge(X((wlq.x1 + wlq.x2) / 2 + vq.ux * vq.len * .3) + vq.nx * (wlq.th / 2 * z + 12),
                         Y((wlq.y1 + wlq.y2) / 2 + vq.uy * vq.len * .3) + vq.ny * (wlq.th / 2 * z + 12));
          });
          if (opts.live) { c._hit = hs[0]; c._hit2 = hs[1]; }
        }
      }
      else if (c.type === 'axis' || c.type === 'fix') {
        const A = doc.walls.find(w => w.id === c.a);
        if (!A) continue;
        const vA = wallVec(A);
        // opposite side from the dimension text (fix sits a step further out
        // so it can coexist with an H/V badge)
        const off = A.th / 2 * z + (c.type === 'fix' ? 28 : 12);
        const px = X((A.x1 + A.x2) / 2) + vA.nx * off;
        const py = Y((A.y1 + A.y2) / 2) + vA.ny * off;
        const btxt = c.type === 'fix' ? '📌' : c.axis === 'h' ? 'H' : 'V';
        g.font = '600 10px "Avenir Next",system-ui,sans-serif';
        const btw = textW(g, btxt);
        g.fillStyle = C.bg; g.globalAlpha = .85; g.fillRect(px - btw / 2 - 3, py - 7, btw + 6, 14); g.globalAlpha = 1;
        g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(btxt, px, py);
        if (opts.live) c._hit = { x: px, y: py, ang: 0, hw: Math.max(btw / 2 + 5, 9), hh: 8 };
      }
      else if (c.type === 'equal' || c.type === 'colin') {
        const A = doc.walls.find(w => w.id === c.a), B = doc.walls.find(w => w.id === c.b);
        if (!A || !B) continue;
        const gl = c.type === 'equal' ? '=' : '≡';
        g.font = '600 11px ui-monospace,Menlo,monospace';
        let first = true;
        for (const wlq of [A, B]) {
          const vq = wallVec(wlq);
          const px = X((wlq.x1 + wlq.x2) / 2 + vq.ux * vq.len * .22) + vq.nx * (wlq.th / 2 * z + 11);
          const py = Y((wlq.y1 + wlq.y2) / 2 + vq.uy * vq.len * .22) + vq.ny * (wlq.th / 2 * z + 11);
          g.fillStyle = C.bg; g.globalAlpha = .85; g.fillRect(px - 7, py - 7, 14, 14); g.globalAlpha = 1;
          g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'middle';
          g.fillText(gl, px, py);
          if (opts.live && first) c._hit = { x: px, y: py, ang: 0, hw: 9, hh: 8 };
          first = false;
        }
      }
      else if (c.type === 'coin' || c.type === 'mid') {
        // point-tie badge (⚯ coin = solid dot in a ring · ⋈ midpoint = hollow
        // ring), hung BESIDE the wall a step in from the tied end so it never
        // covers the endpoint itself — clicks there must select/drag the wall
        const A = doc.walls.find(w => w.id === c.a);
        if (!A) continue;
        const vA = wallVec(A);
        const ex = c.end ? A.x2 : A.x1, ey = c.end ? A.y2 : A.y1;
        const dir = c.end ? -1 : 1;   // inward along the wall from the tied end
        const inset = 14 / z;
        const px = X(ex + dir * vA.ux * inset) + vA.nx * (A.th / 2 * z + 10);
        const py = Y(ey + dir * vA.uy * inset) + vA.ny * (A.th / 2 * z + 10);
        g.beginPath(); g.arc(px, py, 6, 0, Math.PI * 2);
        g.fillStyle = C.bg; g.fill();
        g.lineWidth = 2; g.strokeStyle = C.accent; g.stroke();
        if (c.type === 'coin') {
          g.beginPath(); g.arc(px, py, 3, 0, Math.PI * 2);
          g.fillStyle = C.accent; g.fill();
        }
        if (opts.live) c._hit = { x: px, y: py, ang: 0, hw: 9, hh: 9 };
      }
    }
  }
  /* opening widths (selected/hovered only) */
  if (showDims) {
    for (const o of doc.openings) {
      if (!(sel.has(o.id) || hover === o.id)) continue;
      const wl = doc.walls.find(w => w.id === o.wallId); if (!wl) continue;
      const c = wallPt(wl, o.t);
      g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = '11px ui-monospace,Menlo,monospace';
      g.fillText(fmtLen(o.width), X(c.x), Y(c.y) - wl.th / 2 * z - 16);
    }
    for (const f of doc.fixtures) {
      if (!sel.has(f.id)) continue;
      g.fillStyle = C.accent; g.textAlign = 'center'; g.textBaseline = 'top';
      g.font = '11px ui-monospace,Menlo,monospace';
      const r = fixtureScreenRadius(f) * z;
      g.fillText(`${fmtLen(f.w)} × ${fmtLen(f.h)}`, X(f.x), Y(f.y) + r + 8);
    }
  }

  /* ---- live tool previews ---- */
  if (opts.live) {
    drawPreviews(g, X, Y, z, C);
  }
  g.restore();
}

export const fixtureScreenRadius = (f: { rot: number; w: number; h: number }) => {
  const a = rad(f.rot || 0), cw = Math.abs(Math.cos(a)), sw = Math.abs(Math.sin(a));
  return (f.h * cw + f.w * sw) / 2;
};

function drawFixtureSel(g: CanvasRenderingContext2D, f: any, X: (v: number) => number, Y: (v: number) => number,
                        z: number, C: Colors, isSel: boolean): void {
  g.save();
  g.translate(X(f.x), Y(f.y)); g.rotate(rad(f.rot || 0));
  const w2 = f.w / 2 * z, h2 = f.h / 2 * z;
  g.strokeStyle = C.accent; g.lineWidth = isSel ? 1.5 : 1;
  g.setLineDash(isSel ? [] : [4, 3]);
  g.strokeRect(-w2 - 3, -h2 - 3, w2 * 2 + 6, h2 * 2 + 6);
  g.setLineDash([]);
  if (isSel && state.sel.size === 1) {
    g.fillStyle = C.panel; g.strokeStyle = C.accent; g.lineWidth = 1.4;
    for (const [hx, hy] of [[-w2, -h2], [w2, -h2], [w2, h2], [-w2, h2]] as const) {
      g.beginPath(); g.rect(hx - 3.5, hy - 3.5, 7, 7); g.fill(); g.stroke();
    }
    // rotate handle
    g.beginPath(); g.moveTo(0, -h2 - 3); g.lineTo(0, -h2 - 18); g.stroke();
    g.beginPath(); g.arc(0, -h2 - 22, 4.5, 0, 7); g.fill(); g.stroke();
  }
  g.restore();
}

function drawPreviews(g: CanvasRenderingContext2D, X: (v: number) => number, Y: (v: number) => number,
                      z: number, C: Colors): void {
  const p = state.placePreview;
  /* collaborators' cursors: a dot with a name tag, in their colour */
  const me = (window as any).__phUser?.uid || '';
  for (const o of others(state.presence, me, Date.now())) {
    const px = X(o.x), py = Y(o.y);
    g.save();
    g.fillStyle = o.color; g.strokeStyle = C.panel; g.lineWidth = 1.5;
    g.beginPath(); g.moveTo(px, py); g.lineTo(px + 11, py + 4); g.lineTo(px + 5, py + 6); g.lineTo(px + 3, py + 12); g.closePath(); g.fill(); g.stroke();
    const tag = o.name ? initials(o.name) : '?';
    g.font = '600 10px "Avenir Next",system-ui,sans-serif'; g.textBaseline = 'middle'; g.textAlign = 'center';
    const w = g.measureText(tag).width + 10;
    g.beginPath(); g.roundRect(px + 12, py + 8, w, 16, 4); g.fill();
    g.fillStyle = '#fff'; g.fillText(tag, px + 12 + w / 2, py + 16);
    g.restore();
  }
  /* empty-state hint: nothing drawn yet and nothing in progress */
  $('#emptyHint').classList.toggle('show',
    state.doc.walls.length === 0 && !state.roomDraft && !state.wallDraft && !state.shareView);
  /* room draft */
  if (state.roomDraft) {
    const r = normRect(state.roomDraft.ax, state.roomDraft.ay, state.roomDraft.bx, state.roomDraft.by);
    const ok = r.x2 - r.x1 >= ROOM_MIN && r.y2 - r.y1 >= ROOM_MIN;
    g.strokeStyle = C.accent; g.globalAlpha = .8; g.lineWidth = ROOM_TH * z; g.lineJoin = 'miter';
    g.strokeRect(X(r.x1), Y(r.y1), (r.x2 - r.x1) * z, (r.y2 - r.y1) * z);
    g.globalAlpha = 1;
    const c = clearSize(r);
    positionLenBox(X((r.x1 + r.x2) / 2), Y((r.y1 + r.y2) / 2),
      ok ? `${fmtLen(c.w)} × ${fmtLen(c.h)} inside` : 'drag to size the room', !ok);
  }
  /* wall draft */
  else if (state.tool === 'wall' && state.wallDraft && p) {
    const d = state.wallDraft;
    const end = wallDraftEnd(d, p);
    g.strokeStyle = C.accent; g.globalAlpha = .8; g.lineWidth = 4.5 * z; g.lineCap = 'square';
    g.beginPath(); g.moveTo(X(d.ax), Y(d.ay)); g.lineTo(X(end.x), Y(end.y)); g.stroke();
    g.globalAlpha = 1;
    const len = Math.hypot(end.x - d.ax, end.y - d.ay);
    positionLenBox(X((d.ax + end.x) / 2), Y((d.ay + end.y) / 2), d.typed !== '' ? d.typed : fmtLen(len), d.typed !== '');
  } else if (state.tool === 'wall' && p) {
    const s0 = snapPt(p);   // records snapInd so the join ring shows at the start point too
    g.fillStyle = C.accent;
    g.beginPath(); g.arc(X(s0.x), Y(s0.y), 3.5, 0, 7); g.fill();
    hideLenBox();
  } else hideLenBox();
  /* wall endpoint handles for selected wall */
  if (state.tool === 'select' && state.sel.size === 1) {
    const r = findItem([...state.sel][0]);
    if (r && r.kind === 'walls') {
      const wl = r.item as Wall;
      g.fillStyle = C.panel; g.strokeStyle = C.accent; g.lineWidth = 1.5;
      for (const [x, y] of [[wl.x1, wl.y1], [wl.x2, wl.y2]] as const) {
        g.beginPath(); g.arc(X(x), Y(y), 5, 0, 7); g.fill(); g.stroke();
      }
    }
  }
  /* opening placement preview */
  if (state.tool in OPENING_DEFS && p) {
    const near = nearestWall(state.doc.walls, p, 14 / z);
    if (near) {
      const wl = near.wall, width = OPENING_DEFS[state.tool as OpeningType].width;
      const v2 = wallVec(wl);
      const t = clamp(snapV(near.t), width / 2, Math.max(width / 2, v2.len - width / 2));
      const c = wallPt(wl, t), ang = Math.atan2(v2.uy, v2.ux);
      g.save();
      g.translate(X(c.x), Y(c.y)); g.rotate(ang);
      g.fillStyle = C.accentSoft; g.strokeStyle = C.accent; g.lineWidth = 1.5;
      g.beginPath(); g.rect(-width / 2 * z, -wl.th / 2 * z - 3, width * z, wl.th * z + 6); g.fill(); g.stroke();
      g.restore();
    }
  }
  /* fixture placement preview */
  if (state.tool === 'fixture' && p && state.placeGhost) {
    const def = FIXTURES[state.fixtureType];
    const f = state.placeGhost;
    g.save();
    g.translate(X(f.x!), Y(f.y!)); g.rotate(rad(f.rot)); g.scale(z, z);
    g.strokeStyle = C.accent; g.globalAlpha = .85; g.lineWidth = 1.4 / z; g.lineCap = 'round'; g.lineJoin = 'round';
    def.draw(g, f.w, f.h);
    g.restore(); g.globalAlpha = 1;
  }
  /* measure */
  if (state.measure) {
    const m = state.measure;
    g.strokeStyle = C.danger; g.lineWidth = 1.4; g.setLineDash([6, 4]);
    g.beginPath(); g.moveTo(X(m.ax), Y(m.ay)); g.lineTo(X(m.bx), Y(m.by)); g.stroke();
    g.setLineDash([]);
    for (const [x, y] of [[m.ax, m.ay], [m.bx, m.by]] as const) {
      g.beginPath(); g.arc(X(x), Y(y), 3, 0, 7); g.fillStyle = C.danger; g.fill();
    }
    const len = Math.hypot(m.bx - m.ax, m.by - m.ay);
    if (len > 0.1) {
      g.font = '11.5px ui-monospace,Menlo,monospace';
      const txt = fmtLen(len), tw = textW(g, txt);
      const cx = X((m.ax + m.bx) / 2), cy = Y((m.ay + m.by) / 2) - 12;
      g.fillStyle = C.panel; g.fillRect(cx - tw / 2 - 4, cy - 9, tw + 8, 17);
      g.strokeStyle = C.danger; g.lineWidth = 1; g.strokeRect(cx - tw / 2 - 4, cy - 9, tw + 8, 17);
      g.fillStyle = C.danger; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(txt, cx, cy);
    }
  }
  /* marquee */
  if (state.drag && state.drag.kind === 'marquee') {
    const d = state.drag;
    g.strokeStyle = C.accent; g.fillStyle = C.accentSoft; g.lineWidth = 1;
    const x = Math.min(X(d.ax), X(d.bx)), y = Math.min(Y(d.ay), Y(d.by));
    const w = Math.abs(X(d.bx) - X(d.ax)), h = Math.abs(Y(d.by) - Y(d.ay));
    g.fillRect(x, y, w, h); g.strokeRect(x + .5, y + .5, w, h);
  }
  /* smart guides (alignment lines, ⊥ glyphs) */
  for (const gd of state.guides) {
    g.strokeStyle = C.accent;
    if (gd.kind === 'line') {
      g.lineWidth = 1; g.setLineDash([5, 4]);
      g.beginPath(); g.moveTo(X(gd.x1), Y(gd.y1)); g.lineTo(X(gd.x2), Y(gd.y2)); g.stroke();
      g.setLineDash([]);
      g.fillStyle = C.accent;
      g.beginPath(); g.arc(X(gd.x1), Y(gd.y1), 2.4, 0, 7); g.fill();
    } else if (gd.kind === 'perp') {
      const r = 13, px = X(gd.x), py = Y(gd.y);
      const c1 = Math.cos(gd.a1), s1 = Math.sin(gd.a1), c2 = Math.cos(gd.a2), s2 = Math.sin(gd.a2);
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(px + c1 * r, py + s1 * r);
      g.lineTo(px + (c1 + c2) * r, py + (s1 + s2) * r);
      g.lineTo(px + c2 * r, py + s2 * r);
      g.stroke();
    } else if (gd.kind === 'face') {
      const wl = gd.wall;
      g.save();
      g.strokeStyle = C.accent; g.globalAlpha = .35; g.lineWidth = wl.th * z + 6; g.lineCap = 'butt';
      g.beginPath(); g.moveTo(X(wl.x1), Y(wl.y1)); g.lineTo(X(wl.x2), Y(wl.y2)); g.stroke();
      g.restore();
    } else if (gd.kind === 'ref') {
      // light up the wall the dragged/drawn wall is ⊥/∥ to, badge the relation
      const wl = gd.wall;
      g.save();
      g.strokeStyle = C.accent; g.globalAlpha = .35;
      g.lineWidth = wl.th * z + 6; g.lineCap = 'square';
      g.beginPath(); g.moveTo(X(wl.x1), Y(wl.y1)); g.lineTo(X(wl.x2), Y(wl.y2)); g.stroke();
      g.globalAlpha = 1;
      const px = X(gd.bx), py = Y(gd.by);
      g.fillStyle = C.panel;
      g.beginPath(); g.arc(px, py, 9, 0, 7); g.fill();
      g.strokeStyle = C.accent; g.lineWidth = 1.2; g.stroke();
      g.fillStyle = C.accent; g.font = '600 11px -apple-system, sans-serif';
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(gd.rel, px, py + .5);
      g.restore();
    }
  }
  /* join indicator ring */
  if (state.snapInd) {
    const si = state.snapInd;
    if ((si.type === 'edge' || si.type === 'mid') && si.wall) {
      g.strokeStyle = C.accent; g.lineWidth = 1; g.setLineDash([5, 4]);
      g.beginPath(); g.moveTo(X(si.wall.x1), Y(si.wall.y1)); g.lineTo(X(si.wall.x2), Y(si.wall.y2)); g.stroke();
      g.setLineDash([]);
    }
    g.strokeStyle = C.accent; g.fillStyle = C.panel; g.lineWidth = 2;
    g.beginPath(); g.arc(X(si.x), Y(si.y), 7, 0, 7); g.fill(); g.stroke();
    g.fillStyle = C.accent;
    g.beginPath(); g.arc(X(si.x), Y(si.y), 2.2, 0, 7); g.fill();
    g.font = '600 9.5px "Avenir Next",system-ui,sans-serif';
    g.textAlign = 'left'; g.textBaseline = 'middle';
    g.fillText(si.type === 'end' ? 'join' : si.type === 'mid' ? 'midpoint' : 'on wall', X(si.x) + 12, Y(si.y) - 11);
  }
}
/* ---- floating length box while drawing walls ---- */
const lenBox = $('#lenBox');
export function positionLenBox(px: number, py: number, text: string, typed: boolean): void {
  lenBox.style.display = 'block';
  lenBox.style.left = (px + 14) + 'px'; lenBox.style.top = (py + 14) + 'px';
  lenBox.textContent = text;
  lenBox.style.borderColor = typed ? 'var(--danger)' : 'var(--accent)';
}
export function hideLenBox(): void { lenBox.style.display = 'none'; }

/* ---- view helpers ---- */
export function zoomAt(sp: { x: number; y: number }, nz: number): void {
  const v = state.view;
  const wx = v.x + sp.x / v.zoom, wy = v.y + sp.y / v.zoom;
  v.zoom = nz;
  v.x = wx - sp.x / nz; v.y = wy - sp.y / nz;
  $('#zoomPct').textContent = Math.round(nz / 4 * 100) + '%';
  requestDraw();
}
export function fitView(): void {
  const bb = docBBox(state.doc);
  if (!bb) { state.view = { x: -40, y: -30, zoom: 4 }; requestDraw(); return; }
  const pad = 60;
  const zw = (CSS_W - pad * 2) / Math.max(1, (bb.x2 - bb.x1));
  const zh = (CSS_H - pad * 2) / Math.max(1, (bb.y2 - bb.y1));
  const z = clamp(Math.min(zw, zh), .25, 20);
  state.view.zoom = z;
  state.view.x = (bb.x1 + bb.x2) / 2 - CSS_W / 2 / z;
  state.view.y = (bb.y1 + bb.y2) / 2 - CSS_H / 2 / z;
  $('#zoomPct').textContent = Math.round(z / 4 * 100) + '%';
  requestDraw();
}

/** The plan as a clean, print-ready image: light colours, dimensions and
    room sizes, a title line. 2× pixel density; null when there's nothing to draw. */
export function renderExportCanvas(): HTMLCanvasElement | null {
  const bb = docBBox(state.doc);
  if (!bb) return null;
  const pad = 30; // inches
  const bw = bb.x2 - bb.x1 + pad * 2, bh = bb.y2 - bb.y1 + pad * 2 + 14;   // room for the title line
  const ppi = Math.min(10, 2800 / Math.max(bw, bh));
  const W = Math.round(bw * ppi), H = Math.round(bh * ppi);
  const cv = document.createElement('canvas');
  cv.width = W * 2; cv.height = H * 2;
  const g = cv.getContext('2d')!;
  g.setTransform(2, 0, 0, 2, 0, 0);
  const lightC: Colors = { bg: '#FFFFFF', grid: '#EFF1EF', grid2: '#E2E5E2', ink: '#1E2523', wall: '#39413D',
                           accent: '#0E7C86', accentSoft: 'rgba(14,124,134,.14)', muted: '#78827E', dim: '#5B7B76',
                           panel: '#FFFFFF', danger: '#B4472F' };
  drawScene(g, { z: ppi, ox: bb.x1 - pad, oy: bb.y1 - pad, w: W, h: H }, lightC, { live: false });
  // title line along the bottom
  g.save();
  g.fillStyle = lightC.ink; g.font = `600 ${Math.max(11, Math.min(18, 1.6 * ppi))}px "Avenir Next",system-ui,sans-serif`;
  g.textBaseline = 'bottom'; g.textAlign = 'left';
  g.fillText(state.name || 'Floor plan', 10 * ppi / 3 + 6, H - 8);
  g.fillStyle = lightC.muted; g.font = `${Math.max(9, Math.min(13, 1.2 * ppi))}px "Avenir Next",system-ui,sans-serif`;
  g.textAlign = 'right';
  g.fillText(`${new Date().toLocaleDateString()} · playout.homes`, W - 10, H - 8);
  g.restore();
  return cv;
}

export function exportPNG(): void {
  const cv = renderExportCanvas();
  if (!cv) return;
  const a = document.createElement('a');
  a.download = (state.name || 'plan') + '.png';
  a.href = cv.toDataURL('image/png');
  a.click();
}
